/**
 * 轻量 toast —— 替代源项目用的 `react-hot-toast`（那是 React 专有库）。
 *
 * 用法与 react-hot-toast 的 `toast(message, options)` 一致，只是把渲染交给 `ToastHost.vue`：
 *   import { toast } from '@/lib/toast';
 *   toast('This meeting is being recorded', { duration: 3000, icon: '🎥', position: 'top-center' });
 *
 * 为什么自己写而不找一个 Vue 的 toast 库：本项目只有一处用到它（录制提示条），
 * 为一个提示条引一个第三方库不划算，而且自己写能保证 className/style 透传语义与原实现一致。
 */
import { ref } from 'vue';

export type ToastPosition = 'top-center' | 'top-right' | 'bottom-center' | 'bottom-right';

export interface ToastOptions {
  /** 毫秒；<= 0 表示不自动消失 */
  duration?: number;
  /** 前置图标（emoji 或字符） */
  icon?: string;
  position?: ToastPosition;
  /** 追加到 toast 元素上的 class（原代码传的是 'lk-button'） */
  className?: string;
  /** 内联样式（原代码用它覆盖成危险色） */
  style?: Record<string, string>;
}

export interface ToastItem extends ToastOptions {
  id: number;
  message: string;
}

/** 与 react-hot-toast 的默认时长保持一致 */
const DEFAULT_DURATION = 4000;

export const toasts = ref<ToastItem[]>([]);
let seq = 0;

export function toast(message: string, options: ToastOptions = {}): number {
  const id = ++seq;
  toasts.value = [...toasts.value, { id, message, ...options }];
  const duration = options.duration ?? DEFAULT_DURATION;
  if (duration > 0) {
    setTimeout(() => dismissToast(id), duration);
  }
  return id;
}

export function dismissToast(id: number) {
  toasts.value = toasts.value.filter((t) => t.id !== id);
}
